import React, { useCallback, useEffect, useMemo, useState } from 'react'

import TablePagination from '../components/Tables/TablePagination'

type PaginationResult = {
  controls: React.ReactNode
  count: number
  page: number
  paginate: <T>(items: Array<T>) => Array<T>
  reset: () => void
  rowsPerPage: number
  setCount: (c: number) => void
  limit: number
  offset: number
}

export const DEFAULT_ROWS_PER_PAGE = 10

function usePagination(filterChanged: string, itemsPerPage?: number): PaginationResult {
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const rowsPerPage = useMemo(() => itemsPerPage || DEFAULT_ROWS_PER_PAGE, [itemsPerPage])

  const reset = useCallback(() => {
    setPage(1)
  }, [])

  const onChange = useCallback((number: number) => {
    setPage(number)
  }, [])

  useEffect(() => {
    if (filterChanged) {
      setPage(1)
    }
  }, [filterChanged])

  const controls = useMemo(
    () =>
      count > 0 && (
        <TablePagination
          page={page}
          count={Math.ceil(count / rowsPerPage)}
          onPageChange={onChange}
        />
      ),
    [onChange, page, count]
  )

  const paginate = useCallback<PaginationResult['paginate']>(
    items => items.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [page]
  )

  return {
    controls,
    count,
    page,
    rowsPerPage,
    paginate,
    setCount,
    reset,
    limit: rowsPerPage,
    offset: (page - 1) * rowsPerPage,
  }
}

export default usePagination
